import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { ProductPackage } from "../../types/package";
import { CreatePackageRequestBody } from "./packageRequestBody";
import { initialNewPackage } from "./packageInitialState";
import { SelectAddPackageToChapel, SelectedPackageDetails } from "./packageInterface";

export type PackageType = 'package' | 'product' 
export type PackageStatus = 'activate' | 'inactivate'

interface PackageDetails {
    packageName: string;
    packageDescription: string;
    packageTotalPrice: number;
    packageBudget: number;
    items: ProductPackage[]
    newPackageTitleData: CreatePackageRequestBody;
    packageData: SelectedPackageDetails | null
    addPackageToChapel: SelectAddPackageToChapel[]
    packageTabs: PackageType,
    selectedPackageId: number | null
    packageDetailsAction: {
        packageType: null | 'basic' | 'additional' | 'special';
        typeOfAction: null | 'newPackage' | 'updatePackage'
    },
    // updatePackageType: null | 'basic' | 'additional' | 'special'
    packageStatusUpdate: {
        status: 'activate' | 'inactivate' | null,
        packageName: string;
    }
}

const initialState: PackageDetails  = {
    packageName: '',
    packageDescription: '',
    packageTotalPrice: 0,
    packageBudget: 0,
    items: [],
    newPackageTitleData: initialNewPackage,
    packageData: null,
    addPackageToChapel: [],
    packageTabs: 'package',
    selectedPackageId: null,
    // updatePackageType: null,
    packageDetailsAction: {
        packageType: null,
        typeOfAction: null,
    },
    packageStatusUpdate: {
        status: null,
        packageName: '',
    }
};

const packageSlice = createSlice({
    name: 'package',
    initialState,
    reducers: {
        setPackageStatus: ( state, action: PayloadAction<Partial<{ status: 'activate' | 'inactivate' | null; packageName: string; }>> ) => {
            state.packageStatusUpdate = {
                ...state.packageStatusUpdate,
                ...action.payload,
            };
        },
        setPackageDetails: (state, action: PayloadAction<{packageType: null | 'basic' | 'additional' | 'special'; typeOfAction: null | 'newPackage' | 'updatePackage'}>) => {
            state.packageDetailsAction = {
                ...state.packageDetailsAction,
                ...action.payload,
            };
        },
        setPackage: (state, action: PayloadAction<PackageDetails>) => {
            state.packageName = action.payload.packageName;
            state.packageDescription = action.payload.packageDescription;
            state.packageTotalPrice = action.payload.packageTotalPrice;
            state.packageBudget = action.payload.packageBudget;
            state.items = action.payload.items;
        },
        clearPackage: (state) => {
            state.packageName = '';
            state.packageDescription = '';
            state.packageTotalPrice = 0;
            state.packageBudget = 0;
            state.items = [];
        },

        setPackageInfo: (
            state,
            action: PayloadAction<{
                packageName?: string;
                packageDescription?: string;
                packageBudget?: number;
            }>
            ) => {
            if (action.payload.packageName !== undefined)
                state.packageName = action.payload.packageName;

            if (action.payload.packageDescription !== undefined)
                state.packageDescription = action.payload.packageDescription;

            if (action.payload.packageBudget !== undefined)
                state.packageBudget = action.payload.packageBudget;
        },

        // ADD THIS: Add a single item to the package
        addItem: (state, action: PayloadAction<ProductPackage>) => {
            const existingIndex = state.items.findIndex(
                item => item.productId === action.payload.productId && 
                item.productSize === action.payload.productSize
            );

            if (existingIndex === -1) {
                // Only add if it doesn't already exist
                state.items.push(action.payload);
            }

            // Update total price
            state.packageTotalPrice = state.items.reduce(
                (total, item) => total + item.productPrice,
                0
            );
        },

        // REMOVE an item from the package
        removeItem: (state, action: PayloadAction<{ productId: number }>) => {
            state.items = state.items.filter(
                i => i.productId !== action.payload.productId
            );

            // Recalculate total price
            state.packageTotalPrice = state.items.reduce(
                (total, item) => total + item.productPrice,
                0
            );
        },

        setPackageData: (state, action: PayloadAction<SelectedPackageDetails>) => {
            state.packageData = action.payload;
        },
        clearPackageData: (state) => {
            state.packageData = null;
        },

        // addPackageToChapel
        addPackageToChapel: ( state, action: PayloadAction<SelectAddPackageToChapel> ) => {
            state.addPackageToChapel.push(action.payload);
        },

        removePackageFromChapel: (state, action: PayloadAction<number>) => {
            state.addPackageToChapel = state.addPackageToChapel.filter(
                (pkg) => pkg.packageID !== action.payload
            );
        },

        clearPackagesFromChapel: (state) => {
            state.addPackageToChapel = [];
        },

        updateNewPackageTitleData: (
            state,
            action: PayloadAction<Partial<CreatePackageRequestBody>>) => {
            state.newPackageTitleData = {
                ...state.newPackageTitleData,
                ...action.payload,
            };
        },

        setPackageTab: (state, action: PayloadAction<PackageType>) => {
            state.packageTabs = action.payload;
        },

        setSelectedPackageId: (state, action: PayloadAction<number>) => {
            state.selectedPackageId = action.payload;
        },
        clearSelectedPackageId: (state) => {
            state.selectedPackageId = null;
        },

        resetNewPackageTitleData: (state) => {
            state.newPackageTitleData = initialNewPackage;
        },
        resetPackageState: () => initialState,
    },
});

export const { 
    setPackageStatus,
    
    setPackageDetails,

    setPackage, 
    clearPackage, 
    addItem, 
    removeItem,
    setPackageInfo,
    setPackageData, 
    clearPackageData,
    addPackageToChapel,
    removePackageFromChapel,
    clearPackagesFromChapel,
    updateNewPackageTitleData,
    setPackageTab,
    setSelectedPackageId,
    clearSelectedPackageId,
    resetNewPackageTitleData,
    resetPackageState
} = packageSlice.actions;
export default packageSlice.reducer;

export const selectCurrentPackage = (state: RootState) => state.package;
export const selectCurrentPackageItems = (state: RootState) => state.package.items;
export const selectSelectedPackageData = (state: RootState) => state.package.packageData;
export const selectSelectedPackageDataId = (state: RootState) => state.package.packageData?.packageID;
export const selectedNewPackageTitleData = (state: RootState) => state.package.newPackageTitleData;
export const selectSelectedAddPackageToChapel = (state: RootState) => state.package.addPackageToChapel;

export const selectPackagedId = (state: RootState) => state.package.selectedPackageId;

export const selectPackageTabs = (state: RootState) => state.package.packageTabs;

export const selectPackageDetailsAction = (state: RootState) => state.package.packageDetailsAction;

export const selectPackageStatus = (state: RootState) => state.package.packageStatusUpdate;